import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  signIn,
  signUp,
  confirmSignUp,
  requestPasswordReset,
  confirmPasswordReset,
  respondToMfaChallenge,
  checkAuth,
} from "../../awsAuth";

import logoImage from "./img/logo.png";
import showIcon from "./img/show.png";
import hideIcon from "./img/hide.png";
import emailIcon from "./img/email.png";
import passwordIcon from "./img/password.png";
import phoneIcon from "./img/phone.png";
import "./Auth.scss";

const tipsPool = [
  "Шукаємо найкращі пропозиції…",
  "Готуємо вашу вітрину…",
  "Розкладаємо оголошення по поличках…",
  "Збираємо покупців і продавців докупи…",
  "Перевіряємо нові знахідки…",
  "Відкриваємо двері у Cvanto…",
  "Налаштовуємо ваш простір для продажів…",
  "Готуємо стрічку цікавих пропозицій…",
  "Шукаємо те, що може вас зацікавити…",
  "Підтягуємо свіжі оголошення…",
  "Завантажуємо маркетплейс можливостей…",
  "Зводимо попит із пропозицією…",
  "Допомагаємо знахідкам знаходити власників…",
  "Прокладаємо шлях до вигідних угод…",
  "Запускаємо двигун Cvanto…",
];

function useIOSViewportPatch() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const isIOS =
      /iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    document.documentElement.style.webkitTextSizeAdjust = "100%";

    if (!isIOS) return;

    const meta = document.querySelector('meta[name="viewport"]');

    const addBits = (s) => {
      let c = s || "width=device-width, initial-scale=1";
      if (!/maximum-scale=1/.test(c)) c += ", maximum-scale=1";
      if (!/user-scalable=no/.test(c)) c += ", user-scalable=no";
      if (!/viewport-fit=cover/.test(c)) c += ", viewport-fit=cover";
      return c.replace(/\s+,/g, ",");
    };

    if (meta) {
      meta.setAttribute("content", addBits(meta.getAttribute("content")));
    } else {
      const m = document.createElement("meta");
      m.name = "viewport";
      m.content = addBits();
      document.head.appendChild(m);
    }
  }, []);
}

function useIOSKeyboardLift(containerRef, liftTargetRef) {
  useEffect(() => {
    const root = containerRef.current;
    const target = liftTargetRef.current;
    if (!root || !target) return;

    const isIOS =
      /iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    if (!isIOS || !window.visualViewport) return;

    const vv = window.visualViewport;

    const computeRaise = () => {
      const rect = target.getBoundingClientRect();
      const bottom = rect.bottom;
      const availH = vv.height - (vv.offsetTop || 0);
      const overlap = Math.max(0, bottom - availH + 12);
      const raise = Math.min(overlap, 220);

      root.style.setProperty("--raise", Math.round(raise) + "px");

      if (raise > 0) root.classList.add("kb-open");
      else root.classList.remove("kb-open");
    };

    vv.addEventListener("resize", computeRaise);
    vv.addEventListener("scroll", computeRaise);
    window.addEventListener("orientationchange", computeRaise);

    const onFocus = () => setTimeout(computeRaise, 0);

    root.addEventListener("focusin", onFocus);
    root.addEventListener("focusout", computeRaise);

    computeRaise();

    return () => {
      vv.removeEventListener("resize", computeRaise);
      vv.removeEventListener("scroll", computeRaise);
      window.removeEventListener("orientationchange", computeRaise);
      root.removeEventListener("focusin", onFocus);
      root.removeEventListener("focusout", computeRaise);
      root.classList.remove("kb-open");
      root.style.removeProperty("--raise");
    };
  }, [containerRef, liftTargetRef]);
}

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const MFA_API = `${API_BASE}/api/mfa`;

const Auth = ({ setIsLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialStep = location?.state?.step || "welcome";
  const fromSettings = location?.state?.fromSettings || false;

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const [step, setStep] = useState(initialStep);
  const [transitionKey, setTransitionKey] = useState(0);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(tipsPool[0]);

  const [showPass, setShowPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [mfaUser, setMfaUser] = useState(null);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsAttention, setTermsAttention] = useState(false);

  const containerRef = useRef(null);
  const formWrapperRef = useRef(null);

  useIOSViewportPatch();
  useIOSKeyboardLift(containerRef, formWrapperRef);

  const pickTip = () => {
    setLoadingText(tipsPool[Math.floor(Math.random() * tipsPool.length)]);
  };

  const pulseTerms = () => {
    setTermsAttention(false);

    requestAnimationFrame(() => {
      setTermsAttention(true);

      setTimeout(() => {
        setTermsAttention(false);
      }, 950);
    });
  };

  const changeStep = (next) => {
    if (next === step || isLoading) return;

    setError("");
    setInfo("");
    setTermsAttention(false);
    setTransitionKey((prev) => prev + 1);
    setStep(next);
  };

  const getAuthToken = () => {
    try {
      return localStorage.getItem("authToken") || null;
    } catch {
      return null;
    }
  };

  const ensureToken = async () => {
    let token = getAuthToken();

    if (!token) {
      const ok = await checkAuth();
      if (ok) token = getAuthToken();
    }

    return token;
  };

  const fetchMfaStatus = async () => {
    try {
      const token = await ensureToken();

      const response = await fetch(`${MFA_API}/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });

      if (!response.ok) return false;

      const data = await response.json();
      return !!data.enabled;
    } catch {
      return false;
    }
  };

  const verifyCustomMfa = async (code) => {
    const token = await ensureToken();

    const response = await fetch(`${MFA_API}/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ code }),
    });

    const data = await response.json().catch(() => ({}));

    if (!data.ok) {
      throw new Error("Код невірний або помилка MFA.");
    }
  };

  const mapPasswordError = (message) => {
    if (message.includes("must have numeric characters")) {
      return "Пароль має містити хоча б одну цифру.";
    }

    if (message.includes("must have special characters")) {
      return "Пароль має містити хоча б один спецсимвол.";
    }

    if (message.includes("must have uppercase characters")) {
      return "Пароль має містити хоча б одну велику літеру.";
    }

    if (message.includes("must have lowercase characters")) {
      return "Пароль має містити хоча б одну малу літеру.";
    }

    if (message.includes("minimum length")) {
      return "Пароль має містити щонайменше 8 символів.";
    }

    const details = [];

    if (!/[A-Z]/.test(password)) details.push("одну велику літеру");
    if (!/[a-z]/.test(password)) details.push("одну малу літеру");
    if (!/[0-9]/.test(password)) details.push("цифру");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) details.push("спецсимвол");
    if (password.length < 8) details.push("щонайменше 8 символів");

    return details.length
      ? `Пароль має містити ${details.join(", ")}.`
      : "Пароль не відповідає вимогам.";
  };

  const completeLogin = async () => {
    const mfaEnabled = await fetchMfaStatus();

    if (mfaEnabled) {
      setMfaUser(null);
      setMfaCode("");
      setInfo("Введіть 6-значний код з Authenticator.");
      changeStep("mfa");
      return;
    }

    setIsLoggedIn(true);
    navigate("/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    if (step === "register" && !acceptedTerms) {
      pulseTerms();
      return;
    }

    setError("");
    setInfo("");
    setIsLoading(true);
    pickTip();

    try {
      if (step === "login") {
        const result = await signIn(email.trim(), password);

        if (result?.challengeName === "SOFTWARE_TOKEN_MFA") {
          setMfaUser(result.user);
          changeStep("mfa");
          return;
        }

        await completeLogin();
        return;
      }

      if (step === "mfa") {
        if (mfaUser) {
          await respondToMfaChallenge(mfaUser, mfaCode.trim());
        } else {
          await verifyCustomMfa(mfaCode.trim());
        }

        setIsLoggedIn(true);
        navigate("/");
        return;
      }

      if (step === "register") {
        await signUp(email.trim(), phone.trim(), password);
        changeStep("confirm");
        return;
      }

      if (step === "confirm") {
        await confirmSignUp(email.trim(), confirmationCode.trim());

        const result = await signIn(email.trim(), password);

        if (result?.challengeName === "SOFTWARE_TOKEN_MFA") {
          setMfaUser(result.user);
          changeStep("mfa");
          return;
        }

        await completeLogin();
        return;
      }

      if (step === "forgot") {
        await requestPasswordReset(email.trim());
        setInfo("Код надіслано на email");
        changeStep("resetConfirm");
        return;
      }

      if (step === "resetConfirm") {
        await confirmPasswordReset(
          email.trim(),
          confirmationCode.trim(),
          newPassword
        );

        setInfo("Пароль змінено, тепер увійдіть");
        changeStep("login");
        return;
      }
    } catch (err) {
      console.error("Помилка:", err);

      const message = err?.message || err || "Сталася помилка";

      if (
        step === "register" &&
        String(message).toLowerCase().includes("password")
      ) {
        setError(mapPasswordError(message));
      } else if (String(message).includes("User already exists")) {
        setError("Користувач з таким email вже існує.");
      } else if (String(message).includes("Invalid email")) {
        setError("Невірний формат email.");
      } else {
        setError("❌ " + message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const titleMap = {
    welcome: "",
    login: "Вхід",
    register: "Створити акаунт",
    confirm: "Підтвердження email",
    forgot: "Відновлення паролю",
    resetConfirm: "Новий пароль",
    mfa: "Підтвердження входу",
  };

  const subtitleMap = {
    welcome: "",
    login: "Поверніться до свого простору Cvanto.",
    register: "Створіть профіль і почніть купувати, продавати та спілкуватися.",
    confirm: "Ми надіслали код підтвердження на вашу пошту.",
    forgot: "Введіть email, і ми надішлемо код для зміни паролю.",
    resetConfirm: "Введіть код з email та новий пароль.",
    mfa: "Додатковий рівень безпеки для вашого акаунта.",
  };

  const buttonMap = {
    login: "Увійти",
    register: "Створити акаунт",
    confirm: "Підтвердити",
    forgot: "Надіслати код",
    resetConfirm: "Змінити пароль",
    mfa: "Підтвердити",
  };

  const renderInput = ({
    icon,
    type = "text",
    inputMode,
    autoComplete,
    placeholder,
    value,
    onChange,
    secure,
    visible,
    onToggleVisible,
  }) => {
    return (
      <div className="input-pill">
        <img src={icon} alt="" className="input-icon" />

        <input
          type={secure ? (visible ? "text" : "password") : type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required
          disabled={isLoading}
        />

        {secure && (
          <button
            type="button"
            className={`eye-icon ${visible ? "is-open" : ""}`}
            onClick={onToggleVisible}
            aria-label={visible ? "Приховати пароль" : "Показати пароль"}
            disabled={isLoading}
          >
            <img src={visible ? hideIcon : showIcon} alt="" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className={`auth-container auth-step-${step}`}
      data-step={step}
      ref={containerRef}
    >
      <div className="auth-bg" aria-hidden="true">
        <div className="quantum-glow quantum-glow-main" />
        <div className="quantum-glow quantum-glow-second" />
        <div className="quantum-orbit quantum-orbit-one" />
        <div className="quantum-orbit quantum-orbit-two" />
        <div className="quantum-orbit quantum-orbit-three" />
        <div className="quantum-line quantum-line-one" />
        <div className="quantum-line quantum-line-two" />
        <div className="quantum-particle quantum-particle-one" />
        <div className="quantum-particle quantum-particle-two" />
        <div className="quantum-particle quantum-particle-three" />
        <div className="quantum-particle quantum-particle-four" />
      </div>

      <main className="auth-shell">
        <section className="auth-content">
          <div className="auth-logo">
            <img src={logoImage} alt="Cvanto" />
          </div>

          <div className="auth-hero">
            <h1>
              Купуй. Продавай.
              <br />
              Спілкуйся.
            </h1>

            <p>Соціальний маркетплейс навколо реальних людей.</p>
          </div>

          <div
            className="auth-form-wrapper"
            ref={formWrapperRef}
            key={transitionKey}
          >
            {step === "welcome" ? (
              <div className="welcome-actions">
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => changeStep("register")}
                >
                  Створити акаунт
                </button>

                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => changeStep("login")}
                >
                  Увійти
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className={`auth-form ${isLoading ? "is-loading" : ""}`}
                aria-busy={isLoading}
              >
                <h2>{titleMap[step]}</h2>

                <p className="auth-subtitle">{subtitleMap[step]}</p>

                {error && <p className="error">{error}</p>}
                {info && <p className="info">{info}</p>}

                {step === "login" && (
                  <>
                    {renderInput({
                      icon: emailIcon,
                      type: "email",
                      inputMode: "email",
                      autoComplete: "username email",
                      placeholder: "Email",
                      value: email,
                      onChange: (e) => setEmail(e.target.value),
                    })}

                    {renderInput({
                      icon: passwordIcon,
                      autoComplete: "current-password",
                      placeholder: "Пароль",
                      value: password,
                      onChange: (e) => setPassword(e.target.value),
                      secure: true,
                      visible: showPass,
                      onToggleVisible: () => setShowPass((p) => !p),
                    })}
                  </>
                )}

                {step === "mfa" &&
                  renderInput({
                    icon: passwordIcon,
                    type: "text",
                    inputMode: "numeric",
                    autoComplete: "one-time-code",
                    placeholder: "6-значний код",
                    value: mfaCode,
                    onChange: (e) => setMfaCode(e.target.value),
                  })}

                {step === "register" && (
                  <>
                    {renderInput({
                      icon: emailIcon,
                      type: "email",
                      inputMode: "email",
                      autoComplete: "username email",
                      placeholder: "Email",
                      value: email,
                      onChange: (e) => setEmail(e.target.value),
                    })}

                    {renderInput({
                      icon: phoneIcon,
                      type: "tel",
                      inputMode: "tel",
                      autoComplete: "tel",
                      placeholder: "Телефон з +380",
                      value: phone,
                      onChange: (e) => setPhone(e.target.value),
                    })}

                    {renderInput({
                      icon: passwordIcon,
                      autoComplete: "new-password",
                      placeholder: "Пароль",
                      value: password,
                      onChange: (e) => setPassword(e.target.value),
                      secure: true,
                      visible: showPass,
                      onToggleVisible: () => setShowPass((p) => !p),
                    })}
                  </>
                )}

                {step === "confirm" &&
                  renderInput({
                    icon: emailIcon,
                    type: "text",
                    inputMode: "numeric",
                    autoComplete: "one-time-code",
                    placeholder: "Код з email",
                    value: confirmationCode,
                    onChange: (e) => setConfirmationCode(e.target.value),
                  })}

                {step === "forgot" &&
                  renderInput({
                    icon: emailIcon,
                    type: "email",
                    inputMode: "email",
                    autoComplete: "email",
                    placeholder: "Ваш email",
                    value: email,
                    onChange: (e) => setEmail(e.target.value),
                  })}

                {step === "resetConfirm" && (
                  <>
                    {renderInput({
                      icon: emailIcon,
                      type: "text",
                      inputMode: "numeric",
                      autoComplete: "one-time-code",
                      placeholder: "Код з email",
                      value: confirmationCode,
                      onChange: (e) => setConfirmationCode(e.target.value),
                    })}

                    {renderInput({
                      icon: passwordIcon,
                      autoComplete: "new-password",
                      placeholder: "Новий пароль",
                      value: newPassword,
                      onChange: (e) => setNewPassword(e.target.value),
                      secure: true,
                      visible: showNewPass,
                      onToggleVisible: () => setShowNewPass((p) => !p),
                    })}
                  </>
                )}

                {step === "register" && (
                  <label
                    className={`terms-check ${
                      termsAttention ? "is-attention" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        setTermsAttention(false);
                      }}
                      disabled={isLoading}
                    />

                    <span className="terms-box" aria-hidden="true">
                      {acceptedTerms ? "✓" : ""}
                    </span>

                    <span className="terms-text">
                      Я погоджуюсь з{" "}
                      <Link to="/settings/terms" className="terms-link">
                        Умовами користування
                      </Link>
                      ,{" "}
                      <Link to="/settings/terms" className="terms-link">
                        Правилами спільноти
                      </Link>{" "}
                      та{" "}
                      <Link to="/settings/privacy" className="terms-link">
                        Політикою конфіденційності
                      </Link>
                      .
                    </span>
                  </label>
                )}

                <button
                  type="submit"
                  className={`primary-btn ${
                    step === "register" && !acceptedTerms
                      ? "register-btn-inactive"
                      : ""
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="loading-row">
                      <span className="spinner" />
                      <span>{loadingText}</span>
                    </span>
                  ) : (
                    buttonMap[step]
                  )}
                </button>

                {step === "login" && (
                  <button
                    type="button"
                    className="auth-link-btn"
                    onClick={() => changeStep("forgot")}
                    disabled={isLoading}
                  >
                    Забули пароль?
                  </button>
                )}

                {["login", "register"].includes(step) && (
                  <button
                    type="button"
                    className="auth-link-btn"
                    onClick={() =>
                      changeStep(step === "login" ? "register" : "login")
                    }
                    disabled={isLoading}
                  >
                    {step === "login"
                      ? "Створити акаунт"
                      : "Вже маєте акаунт? Увійти"}
                  </button>
                )}

                {["forgot", "resetConfirm", "confirm", "mfa"].includes(step) &&
                  !fromSettings && (
                    <button
                      type="button"
                      onClick={() => changeStep("login")}
                      className="auth-link-btn"
                      disabled={isLoading}
                    >
                      Назад до входу
                    </button>
                  )}
              </form>
            )}
          </div>

          <button
            type="button"
            className="guest-link"
            onClick={() => navigate("/")}
            disabled={isLoading}
          >
            Продовжити як гість
          </button>
        </section>
      </main>
    </div>
  );
};

export default Auth;